/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

import React, { useState, useEffect, useRef } from 'react';
import {
  createAnalysisDraft,
  deleteAnalysisDraft,
  getAnalysisTypes,
  getAnalysisTypeDetails,
  instantiateAnalysisTemplate,
  listAnalysisDrafts,
  listAnalysisTemplates,
  listAnalysisTemplateVersions,
  planAnalysisJob,
  submitAnalysisJob,
  submitAnalysisDraft,
  updateAnalysisDraft,
} from '../../api/api';
import {
  buildPersistedAnalysisDraft,
  buildPlannerAnalysisDraft,
  buildRecoveryAnalysisDraft,
} from './analysisDrafts';

const availableTimeframes = ["M1", "M5", "M15", "M30", "H1", "H4", "D1", "W1", "MN1"];

const getDefaultSchemaValue = (schema = {}) => {
  if (schema.default !== undefined) {
    return schema.default;
  }

  if (schema.type === 'integer' || schema.type === 'number') {
    return '';
  }

  if (schema.type === 'boolean') {
    return false;
  }

  return '';
};

const buildAnalysisParamsFromSchema = (parameterSchema = {}, detailDefaults = {}) => {
  const nextParams = {};

  Object.entries(parameterSchema).forEach(([name, schema]) => {
    if (detailDefaults[name] !== undefined) {
      nextParams[name] = detailDefaults[name];
      return;
    }

    nextParams[name] = getDefaultSchemaValue(schema);
  });

  return nextParams;
};

const getDateInputValue = (value) => {
  if (typeof value !== 'string') {
    return '';
  }

  return value.slice(0, 10);
};

const prettifyAnalysisType = (value) => {
  if (typeof value !== 'string' || !value.trim()) {
    return 'Template';
  }

  return value
    .split('_')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

const normalizeObject = (value) => (
  value && typeof value === 'object' && !Array.isArray(value) ? value : null
);

const isCapabilityOverviewReview = (reviewPosture) => (
  reviewPosture?.reason === 'capability_overview'
);

const derivePlannerRepresentation = (draft, reviewPosture) => {
  const explicitRepresentation = normalizeObject(reviewPosture?.representation);
  if (explicitRepresentation) {
    return explicitRepresentation;
  }

  const hasQueryPlan = Boolean(normalizeObject(draft?.queryPlan));
  const hasSpec = Boolean(normalizeObject(draft?.spec));
  const plannerSource = typeof draft?.plannerSource === 'string' ? draft.plannerSource : '';

  if (hasQueryPlan && plannerSource === 'llm_compiled_query_plan_v1') {
    return {
      review_surface: 'query_plan',
      primary_artifact: 'query_plan',
      execution_artifact: hasSpec ? 'compiled_draft_spec' : null,
    };
  }

  if (hasSpec) {
    return {
      review_surface: 'draft_spec',
      primary_artifact: 'draft_spec',
      execution_artifact: 'draft_spec',
    };
  }

  if (hasQueryPlan) {
    return {
      review_surface: 'query_plan',
      primary_artifact: 'query_plan',
      execution_artifact: null,
    };
  }

  return {
    review_surface: 'none',
    primary_artifact: null,
    execution_artifact: null,
  };
};

const getPlannerReviewSurfaceLabel = (representation) => {
  if (representation?.review_surface === 'query_plan' && representation?.execution_artifact === 'compiled_draft_spec') {
    return 'Query plan + compiled execution spec';
  }
  if (representation?.review_surface === 'query_plan') {
    return 'Query plan';
  }
  if (representation?.review_surface === 'draft_spec') {
    return 'Direct draft spec';
  }
  return 'No executable artifact yet';
};

const getPlanSpecEditorLabel = (representation) => {
  if (representation?.execution_artifact === 'compiled_draft_spec') {
    return 'Compiled Execution Spec (JSON)';
  }
  return 'Editable Plan Spec (JSON)';
};

const getPlanSpecEditorHelpText = (representation) => {
  if (representation?.execution_artifact === 'compiled_draft_spec') {
    return 'Primary planner artifact is the query plan. The JSON editor below shows the compiled execution spec derived from it.';
  }
  if (representation?.review_surface === 'query_plan') {
    return 'Primary planner artifact is the query plan.';
  }
  return '';
};

const getPlannerOutcomeLabel = (outcome, planReady, reviewPosture = null) => {
  if (isCapabilityOverviewReview(reviewPosture)) {
    return 'Capability Guide';
  }
  if (outcome === 'blocked') {
    return 'Blocked';
  }
  if (outcome === 'preview') {
    return 'Preview';
  }
  if (outcome === 'confirm') {
    return 'Needs Confirmation';
  }
  if (outcome === 'ready' || planReady) {
    return 'Draft Ready';
  }
  return 'Needs Clarification';
};

const getPlannerOutcomeClasses = (outcome, planReady, reviewPosture = null) => {
  if (isCapabilityOverviewReview(reviewPosture)) {
    return 'border border-borderDefault bg-backgroundInput text-textPrimary';
  }
  if (outcome === 'blocked') {
    return 'bg-rose-100 text-rose-700';
  }
  if (outcome === 'preview' || outcome === 'confirm') {
    return 'bg-sky-100 text-sky-700';
  }
  if (outcome === 'ready' || planReady) {
    return 'bg-green-100 text-green-700';
  }
  return 'bg-amber-100 text-amber-700';
};

const getPlannerSupportLabel = (support, reviewPosture = null) => {
  if (isCapabilityOverviewReview(reviewPosture)) {
    return 'Choose a Starting Point';
  }
  if (support === 'current_executable') {
    return 'Current Executor Ready';
  }
  if (support === 'planner_preview_only') {
    return 'Planner Preview Only';
  }
  if (support === 'unsupported') {
    return 'Unsupported Today';
  }
  if (support === 'needs_clarification') {
    return 'Needs More Context';
  }
  return 'Review Support';
};

const getPlannerSupportClasses = (support, reviewPosture = null) => {
  if (isCapabilityOverviewReview(reviewPosture)) {
    return 'border border-borderDefault bg-backgroundInput text-textPrimary';
  }
  if (support === 'current_executable') {
    return 'bg-emerald-100 text-emerald-700';
  }
  if (support === 'unsupported') {
    return 'bg-rose-100 text-rose-700';
  }
  if (support === 'planner_preview_only') {
    return 'bg-sky-100 text-sky-700';
  }
  return 'bg-amber-100 text-amber-700';
};

const getPlannerReadinessLabel = (draft) => {
  if (isCapabilityOverviewReview(draft?.reviewPosture)) {
    return 'Pick a Starting Point';
  }
  if (draft?.submitReady) {
    return 'Submit Ready';
  }
  if (draft?.reviewPosture?.requiresConfirmation) {
    return 'Confirmation Required';
  }
  if (draft?.outcome === 'blocked') {
    return 'Scope Change Required';
  }
  if (draft?.outcome === 'clarify') {
    return 'Reply Needed';
  }
  return 'Review Required';
};

const getPlannerReadinessClasses = (draft) => {
  if (isCapabilityOverviewReview(draft?.reviewPosture)) {
    return 'border border-borderDefault bg-backgroundInput text-textPrimary';
  }
  if (draft?.submitReady) {
    return 'bg-blue-100 text-blue-700';
  }
  if (draft?.outcome === 'blocked') {
    return 'bg-rose-100 text-rose-700';
  }
  if (draft?.reviewPosture?.requiresConfirmation) {
    return 'bg-sky-100 text-sky-700';
  }
  return 'bg-gray-100 text-gray-700';
};

const getPlannerPanelClasses = (outcome, planReady, reviewPosture = null) => {
  if (isCapabilityOverviewReview(reviewPosture)) {
    return 'border-borderDefault bg-backgroundSurface';
  }
  if (outcome === 'blocked') {
    return 'border-rose-200 bg-rose-50';
  }
  if (outcome === 'preview' || outcome === 'confirm') {
    return 'border-sky-200 bg-sky-50';
  }
  if (outcome === 'ready' || planReady) {
    return 'border-emerald-200 bg-emerald-50';
  }
  return 'border-amber-200 bg-amber-50';
};

const formatPlannerReason = (reason) => {
  if (typeof reason !== 'string' || !reason.trim()) {
    return 'Planner review required';
  }
  return reason
    .split('_')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const formatComplexityFeature = (feature) => {
  if (typeof feature !== 'string' || !feature.trim()) {
    return '';
  }
  return feature
    .split('_')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const formatPlannerAction = (action) => {
  if (typeof action !== 'string' || !action.trim()) {
    return 'Review planner output';
  }
  return action
    .split('_')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const buildPlannerAssistantSummary = (draft) => {
  if (!draft) {
    return '';
  }

  const backendSummary = draft?.reviewPosture?.explanation?.detail;
  if (typeof backendSummary === 'string' && backendSummary.trim()) {
    return backendSummary;
  }

  if (draft.outcome === 'blocked') {
    return 'This request is outside the current bounded historical-analysis surface. Narrow the scope or reshape the question and continue the conversation.';
  }
  if (draft.outcome === 'preview') {
    return 'The assistant shaped a preview plan, but the current executable subset cannot run it yet. You can keep refining the request skeleton before execution support expands.';
  }
  if (draft.outcome === 'confirm') {
    return 'The assistant shaped a coherent plan. Review the scope and confirm the draft before submitting it for execution.';
  }
  if (draft.outcome === 'ready' || draft.planReady) {
    return 'The assistant shaped a reviewable plan draft. Inspect the assumptions and JSON, then submit when you are comfortable with the scope.';
  }
  return 'The assistant needs one or more details before it can lock in a valid analysis skeleton. Reply in the research box and continue planning.';
};

const AnalysisJobForm = ({ instruments, onJobSubmitted, onError, recoveryDraft = null }) => {
  // Form state
  const [jobName, setJobName] = useState('');
  const [analysisTypes, setAnalysisTypes] = useState([]);
  const [selectedInstrument, setSelectedInstrument] = useState('');
  const [selectedAnalysisType, setSelectedAnalysisType] = useState('');
  const [targetTimeframe, setTargetTimeframe] = useState('M15');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  
  // Dynamic params state
  const [indicatorConfigs, setIndicatorConfigs] = useState([]);
  const [analysisParams, setAnalysisParams] = useState({});
  const [analysisParamSchema, setAnalysisParamSchema] = useState({});

  // Planner state
  const [plannerPrompt, setPlannerPrompt] = useState('');
  const [plannerAnalysisTypeHint, setPlannerAnalysisTypeHint] = useState('');
  const [plannerTimeframeHint, setPlannerTimeframeHint] = useState('');
  const [isPlanning, setIsPlanning] = useState(false);
  const [activeDraft, setActiveDraft] = useState(null);
  const [savedDrafts, setSavedDrafts] = useState([]);
  const [isLoadingDrafts, setIsLoadingDrafts] = useState(true);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [savedTemplates, setSavedTemplates] = useState([]);
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(true);
  const [instantiatingTemplateId, setInstantiatingTemplateId] = useState(null);
  const [selectedTemplateVersions, setSelectedTemplateVersions] = useState({});
  const [templateVersionsById, setTemplateVersionsById] = useState({});
  const [loadingTemplateVersionsId, setLoadingTemplateVersionsId] = useState(null);
  const [libraryView, setLibraryView] = useState('drafts');

  // UI state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [formError, setFormError] = useState(null);

  const lastAppliedRecoveryDraftIdRef = useRef(null);
  const plannerPromptRef = useRef(null);
  const planSpecTextRef = useRef(null);

  const hasPlanDraft = Boolean(activeDraft?.specText?.trim());
  const activeDraftAnalysisType = activeDraft?.analysisType || activeDraft?.spec?.metadata?.analysis_type || '';
  const activeDraftAnalysisTypeLabel = activeDraftAnalysisType
    ? analysisTypes.find((type) => type.value === activeDraftAnalysisType)?.label || prettifyAnalysisType(activeDraftAnalysisType)
    : '';
  const activePlannerOutcome = activeDraft?.outcome || (activeDraft?.planReady ? 'ready' : null);
  const activeReviewPosture = normalizeObject(activeDraft?.reviewPosture);
  const activePlannerRepresentation = derivePlannerRepresentation(activeDraft, activeReviewPosture);
  const activeClarifyingQuestion = normalizeObject(activeDraft?.clarifyingQuestion);
  const continuationPlannerContext = activeDraft?.sourceType === 'planner'
    ? normalizeObject(activeDraft?.plannerContext)
    : null;
  const planningActionLabel = activeDraft?.sourceType === 'planner'
    ? 'Continue Planning'
    : 'Draft Plan';
  const showManualBuilder = !hasPlanDraft && !isLoadingDetails && (
    analysisTypes.length > 0
    || indicatorConfigs.length > 0
    || Object.keys(analysisParamSchema).length > 0
    || Object.keys(analysisParams).length > 0
  );
  const isManualSubmitReady = Boolean(
    selectedInstrument && selectedAnalysisType && targetTimeframe && startDate && endDate
  );

  // Fetch available analysis types on mount
  useEffect(() => {
    const fetchTypes = async () => {
      try {
        const types = await getAnalysisTypes();
        setAnalysisTypes(types);
        setSelectedAnalysisType((currentValue) => {
          if (currentValue && types.some((type) => type.value === currentValue)) {
            return currentValue;
          }

          return '';
        });
      } catch {
        setFormError("Could not load analysis types.");
        if (onError) onError("Could not load analysis types.");
      }
    };
    fetchTypes();
  }, [onError]);

  useEffect(() => {
    const fetchDrafts = async () => {
      try {
        setIsLoadingDrafts(true);
        const drafts = await listAnalysisDrafts();
        setSavedDrafts(drafts);
      } catch (error) {
        const errorMessage = `Could not load saved drafts: ${error.message}`;
        setFormError(errorMessage);
        if (onError) onError(errorMessage);
      } finally {
        setIsLoadingDrafts(false);
      }
    };

    fetchDrafts();
  }, [onError]);

  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        setIsLoadingTemplates(true);
        const templates = await listAnalysisTemplates();
        setSavedTemplates(templates);
        setSelectedTemplateVersions((currentVersions) => {
          const nextVersions = { ...currentVersions };
          templates.forEach((template) => {
            if (!nextVersions[template.template_id] && Number.isInteger(template.latest_version_number)) {
              nextVersions[template.template_id] = template.latest_version_number;
            }
          });
          return nextVersions;
        });
      } catch (error) {
        const errorMessage = `Could not load saved templates: ${error.message}`;
        setFormError(errorMessage);
        if (onError) onError(errorMessage);
      } finally {
        setIsLoadingTemplates(false);
      }
    };

    fetchTemplates();
  }, [onError]);

  useEffect(() => {
    if (libraryView === 'drafts' && savedDrafts.length === 0 && savedTemplates.length > 0) {
      setLibraryView('templates');
    }
    if (libraryView === 'templates' && savedTemplates.length === 0 && savedDrafts.length > 0) {
      setLibraryView('drafts');
    }
  }, [libraryView, savedDrafts.length, savedTemplates.length]);

  const getTemplateVersionOptions = (templateRecord) => {
    const templateId = templateRecord?.template_id;
    if (!templateId) {
      return [];
    }

    const loadedVersions = templateVersionsById[templateId];
    if (Array.isArray(loadedVersions) && loadedVersions.length > 0) {
      return loadedVersions;
    }

    if (Number.isInteger(templateRecord.latest_version_number)) {
      return [{
        version_number: templateRecord.latest_version_number,
        title: templateRecord.latest_version?.title || templateRecord.name || 'Latest version',
        target_timeframe: templateRecord.latest_version?.target_timeframe || '',
      }];
    }

    return [];
  };

  const ensureTemplateVersionsLoaded = async (templateRecord) => {
    if (!templateRecord?.template_id || templateRecord.version_count <= 1) {
      return;
    }
    if (Array.isArray(templateVersionsById[templateRecord.template_id])) {
      return;
    }
    if (loadingTemplateVersionsId === templateRecord.template_id) {
      return;
    }

    setLoadingTemplateVersionsId(templateRecord.template_id);
    try {
      const versions = await listAnalysisTemplateVersions(templateRecord.template_id);
      setTemplateVersionsById((currentVersions) => ({
        ...currentVersions,
        [templateRecord.template_id]: versions,
      }));
      setSelectedTemplateVersions((currentSelections) => {
        if (currentSelections[templateRecord.template_id]) {
          return currentSelections;
        }

        const firstVersion = versions[0]?.version_number;
        if (!Number.isInteger(firstVersion)) {
          return currentSelections;
        }

        return {
          ...currentSelections,
          [templateRecord.template_id]: firstVersion,
        };
      });
    } catch (error) {
      const errorMessage = `Could not load template versions: ${error.message}`;
      setFormError(errorMessage);
      if (onError) onError(errorMessage);
    } finally {
      setLoadingTemplateVersionsId(null);
    }
  };

  const upsertSavedDraft = (draftRecord) => {
    if (!draftRecord?.draft_id) {
      return;
    }

    setSavedDrafts((currentDrafts) => {
      const existingIndex = currentDrafts.findIndex((draft) => draft.draft_id === draftRecord.draft_id);
      if (existingIndex === -1) {
        return [draftRecord, ...currentDrafts];
      }

      const nextDrafts = [...currentDrafts];
      nextDrafts[existingIndex] = draftRecord;
      return nextDrafts;
    });
  };

  const removeSavedDraft = (draftId) => {
    setSavedDrafts((currentDrafts) => currentDrafts.filter((draft) => draft.draft_id !== draftId));
  };

  const applyDraftToForm = (nextDraft, draftRecord) => {
    if (!nextDraft || !draftRecord) {
      return;
    }

    setActiveDraft(nextDraft);
    setFormError(null);

    setSelectedInstrument(draftRecord.instrument_symbol || '');
    setSelectedAnalysisType(draftRecord.analysis_type || '');
    setPlannerAnalysisTypeHint(draftRecord.analysis_type || '');
    setTargetTimeframe(draftRecord.target_timeframe || 'M15');
    setPlannerTimeframeHint(draftRecord.target_timeframe || '');
    setStartDate(getDateInputValue(draftRecord.start_date || ''));
    setEndDate(getDateInputValue(draftRecord.end_date || ''));
    setJobName(draftRecord.name || '');
    setPlannerPrompt(draftRecord.prompt || '');
  };

  const parseActiveDraftSpec = () => {
    if (!activeDraft?.specText?.trim()) {
      throw new Error('Draft spec is empty.');
    }

    const parsedSpec = JSON.parse(activeDraft.specText);
    if (!parsedSpec || typeof parsedSpec !== 'object' || Array.isArray(parsedSpec)) {
      throw new Error('Plan spec must be a JSON object.');
    }

    return parsedSpec;
  };

  const buildDraftPlannerContext = (parsedSpec) => {
    const nextPlannerContext = {
      ...(normalizeObject(activeDraft?.plannerContext) || {}),
      outcome: activeDraft?.outcome || (activeDraft?.planReady ? 'ready' : 'clarify'),
      analysis_type: activeDraftAnalysisType || parsedSpec?.metadata?.analysis_type || selectedAnalysisType || null,
      instrument_symbol: selectedInstrument || activeDraft?.instrumentSymbol || null,
      planner_source: activeDraft?.plannerSource || null,
      planner_model: activeDraft?.plannerModel || null,
      missing_details: Array.isArray(activeDraft?.missingDetails) ? activeDraft.missingDetails : [],
      query_plan: normalizeObject(activeDraft?.queryPlan),
      clarifying_question: normalizeObject(activeDraft?.clarifyingQuestion),
      review_posture: normalizeObject(activeDraft?.reviewPosture),
    };

    return nextPlannerContext;
  };

  const buildDraftPersistencePayload = (parsedSpec) => ({
    name: jobName || activeDraft?.persistedDraftName || '',
    source_type: activeDraft?.sourceType === 'recovery' ? 'recovery' : activeDraft?.sourceType || 'planner',
    prompt: plannerPrompt,
    instrument_symbol: selectedInstrument || '',
    analysis_spec: parsedSpec,
    planner_context: buildDraftPlannerContext(parsedSpec),
    planning_warnings: activeDraft?.warnings || [],
    assumptions: activeDraft?.assumptions || [],
    missing_details: activeDraft?.missingDetails || [],
  });

  const handleClarifyingChoiceSelect = (choice) => {
    if (typeof choice !== 'string' || !choice.trim()) {
      return;
    }

    const normalizedChoice = `Clarification: ${choice.trim()}`;
    setPlannerPrompt((currentPrompt) => {
      const nextPrompt = typeof currentPrompt === 'string' ? currentPrompt.trimEnd() : '';
      if (!nextPrompt) {
        return normalizedChoice;
      }
      if (nextPrompt.includes(normalizedChoice)) {
        return nextPrompt;
      }
      return `${nextPrompt}\n${normalizedChoice}`;
    });
    plannerPromptRef.current?.focus();
  };

  const handleSaveDraft = async () => {
    if (!hasPlanDraft) {
      return;
    }

    setFormError(null);
    setIsSavingDraft(true);
    try {
      const parsedSpec = parseActiveDraftSpec();
      const draftPayload = buildDraftPersistencePayload(parsedSpec);
      const savedDraft = activeDraft?.persistedDraftId
        ? await updateAnalysisDraft(activeDraft.persistedDraftId, draftPayload)
        : await createAnalysisDraft(draftPayload);

      upsertSavedDraft(savedDraft);
      const persistedDraft = buildPersistedAnalysisDraft(savedDraft);
      applyDraftToForm(persistedDraft, savedDraft);
    } catch (error) {
      const errorMessage = `Saving draft failed: ${error.message}`;
      setFormError(errorMessage);
      if (onError) onError(errorMessage);
    } finally {
      setIsSavingDraft(false);
    }
  };

  const handleLoadSavedDraft = (draftRecord) => {
    setLibraryView('drafts');
    const persistedDraft = buildPersistedAnalysisDraft(draftRecord);
    if (!persistedDraft) {
      const errorMessage = 'Saved draft could not be loaded.';
      setFormError(errorMessage);
      if (onError) onError(errorMessage);
      return;
    }

    applyDraftToForm(persistedDraft, draftRecord);
  };

  const handleDeleteSavedDraft = async (draftId) => {
    try {
      await deleteAnalysisDraft(draftId);
      removeSavedDraft(draftId);
      if (activeDraft?.persistedDraftId === draftId) {
        clearPlanDraft();
      }
    } catch (error) {
      const errorMessage = `Deleting draft failed: ${error.message}`;
      setFormError(errorMessage);
      if (onError) onError(errorMessage);
    }
  };

  const handleInstantiateTemplate = async (templateRecord) => {
    if (!templateRecord?.template_id) {
      return;
    }

    setLibraryView('templates');
    setFormError(null);
    setInstantiatingTemplateId(templateRecord.template_id);
    try {
      const instantiatePayload = {};
      const selectedVersion = selectedTemplateVersions[templateRecord.template_id];
      if (jobName.trim()) {
        instantiatePayload.name = jobName.trim();
      }
      if (selectedInstrument) {
        instantiatePayload.instrument_symbol = selectedInstrument;
      }
      if (Number.isInteger(selectedVersion) && selectedVersion > 0) {
        instantiatePayload.version = selectedVersion;
      }
      if (targetTimeframe) {
        instantiatePayload.target_timeframe = targetTimeframe;
      }
      if (startDate) {
        instantiatePayload.start_date = startDate;
      }
      if (endDate) {
        instantiatePayload.end_date = endDate;
      }

      const draftRecord = await instantiateAnalysisTemplate(templateRecord.template_id, instantiatePayload);
      const persistedDraft = buildPersistedAnalysisDraft(draftRecord);

      if (!persistedDraft) {
        throw new Error('Instantiated template did not return a usable draft.');
      }

      upsertSavedDraft(draftRecord);
      applyDraftToForm(persistedDraft, draftRecord);
    } catch (error) {
      const errorMessage = `Loading template failed: ${error.message}`;
      setFormError(errorMessage);
      if (onError) onError(errorMessage);
    } finally {
      setInstantiatingTemplateId(null);
    }
  };

  useEffect(() => {
    if (!recoveryDraft || typeof recoveryDraft !== 'object' || Array.isArray(recoveryDraft)) {
      return;
    }

    const draftId = recoveryDraft.draft_id;
    if (!draftId || lastAppliedRecoveryDraftIdRef.current === draftId) {
      return;
    }

    if (!recoveryDraft.spec || typeof recoveryDraft.spec !== 'object' || Array.isArray(recoveryDraft.spec)) {
      return;
    }

    lastAppliedRecoveryDraftIdRef.current = draftId;
    setFormError(null);
    const nextDraft = buildRecoveryAnalysisDraft(recoveryDraft);
    if (!nextDraft) {
      return;
    }

    setActiveDraft(nextDraft);

    if (recoveryDraft.instrument_symbol) {
      setSelectedInstrument(recoveryDraft.instrument_symbol);
    }
    if (recoveryDraft.analysis_type) {
      setSelectedAnalysisType(recoveryDraft.analysis_type);
      setPlannerAnalysisTypeHint(recoveryDraft.analysis_type);
    }
    if (recoveryDraft.target_timeframe) {
      setTargetTimeframe(recoveryDraft.target_timeframe);
      setPlannerTimeframeHint(recoveryDraft.target_timeframe);
    }
    if (recoveryDraft.start_date) {
      setStartDate(getDateInputValue(recoveryDraft.start_date));
    }
    if (recoveryDraft.end_date) {
      setEndDate(getDateInputValue(recoveryDraft.end_date));
    }
    if (recoveryDraft.job_name) {
      setJobName(recoveryDraft.job_name);
    }

    if (nextDraft.focusTarget === 'plan_spec') {
      setTimeout(() => {
        planSpecTextRef.current?.focus();
      }, 0);
    }
  }, [recoveryDraft]);

  // Fetch details when analysis type changes
  useEffect(() => {
    if (!selectedAnalysisType) {
      setIndicatorConfigs([]);
      setAnalysisParams({});
      setAnalysisParamSchema({});
      return;
    }

    const fetchDetails = async () => {
      setIsLoadingDetails(true);
      setFormError(null);
      try {
        const details = await getAnalysisTypeDetails(selectedAnalysisType);
        if (Array.isArray(details)) {
          // Handle direct array response for indicators
          setIndicatorConfigs(details);
          setAnalysisParams({});
          setAnalysisParamSchema({});
        } else {
          // Handle object response with keys
          const parameterSchema = details.parameter_schema || {};
          setIndicatorConfigs(details.indicator_configs || []);
          setAnalysisParamSchema(parameterSchema);
          setAnalysisParams(
            buildAnalysisParamsFromSchema(parameterSchema, details.analysis_params || {})
          );
        }
      } catch (error) {
        setIndicatorConfigs([]);
        setAnalysisParams({});
        setAnalysisParamSchema({});
        const errorMessage = `Could not load details for ${selectedAnalysisType}: ${error.message}`;
        setFormError(errorMessage);
        if (onError) onError(errorMessage);
      } finally {
        setIsLoadingDetails(false);
      }
    };

    fetchDetails();
  }, [selectedAnalysisType, onError]);

  const handleIndicatorParamChange = (indicatorIndex, paramName, value) => {
    const updatedConfigs = [...indicatorConfigs];
    updatedConfigs[indicatorIndex].params[paramName] = value;
    setIndicatorConfigs(updatedConfigs);
  };

  const handleAnalysisParamChange = (paramName, value) => {
    setAnalysisParams(prev => ({ ...prev, [paramName]: value }));
  };

  const clearPlanDraft = () => {
    setActiveDraft(null);
  };

  const handlePlanRequest = async () => {
    const normalizedPrompt = plannerPrompt.trim();
    if (!normalizedPrompt) {
      const errorMsg = 'Enter a research question before drafting a plan.';
      setFormError(errorMsg);
      if (onError) onError(errorMsg);
      return;
    }

    setFormError(null);
    setIsPlanning(true);

    const requestPayload = {
      prompt: normalizedPrompt,
    };

    if (plannerAnalysisTypeHint) {
      requestPayload.analysis_type = plannerAnalysisTypeHint;
    }
    if (selectedInstrument) {
      requestPayload.instrument_symbol = selectedInstrument;
    }
    if (plannerTimeframeHint) {
      requestPayload.target_timeframe = plannerTimeframeHint;
    }
    if (startDate) {
      requestPayload.start_date = startDate;
    }
    if (endDate) {
      requestPayload.end_date = endDate;
    }
    if (continuationPlannerContext) {
      requestPayload.planner_context = continuationPlannerContext;
    }

    try {
      const response = await planAnalysisJob(requestPayload);
      setActiveDraft(buildPlannerAnalysisDraft(response));

      if (response.instrument_symbol) {
        setSelectedInstrument(response.instrument_symbol);
      }
      if (response.analysis_type) {
        setSelectedAnalysisType(response.analysis_type);
        setPlannerAnalysisTypeHint(response.analysis_type);
      }

      const responseSpecData = response.draft_spec?.data || {};
      const responseDateRange = responseSpecData.date_range || {};
      if (responseSpecData.timeframe) {
        setTargetTimeframe(responseSpecData.timeframe);
        setPlannerTimeframeHint(responseSpecData.timeframe);
      }
      if (responseDateRange.start) {
        setStartDate(getDateInputValue(responseDateRange.start));
      }
      if (responseDateRange.end) {
        setEndDate(getDateInputValue(responseDateRange.end));
      }
    } catch (error) {
      const errorMessage = `Plan drafting failed: ${error.message}`;
      setFormError(errorMessage);
      if (onError) onError(errorMessage);
    } finally {
      setIsPlanning(false);
    }
  };

  const renderAnalysisParamInput = (paramName, schema = {}) => {
    const fieldId = `analysis-param-${paramName}`;
    const value = analysisParams[paramName] ?? getDefaultSchemaValue(schema);

    if (Array.isArray(schema.enum) && schema.enum.length > 0) {
      return (
        <select
          id={fieldId}
          value={value}
          onChange={(e) => handleAnalysisParamChange(paramName, e.target.value)}
          className="mt-1 block w-full p-1.5 border border-borderDefault rounded-md shadow-sm text-sm bg-backgroundInput text-textPrimary"
        >
          {schema.enum.map((option) => (
            <option key={option} value={option}>{option}</option>
          ))}
        </select>
      );
    }

    if (schema.type === 'boolean') {
      return (
        <input
          type="checkbox"
          id={fieldId}
          checked={Boolean(value)}
          onChange={(e) => handleAnalysisParamChange(paramName, e.target.checked)}
          className="mt-2 h-4 w-4 rounded border-borderDefault text-primary focus:ring-primary"
        />
      );
    }

    const inputType = schema.type === 'integer' || schema.type === 'number' ? 'number' : 'text';

    return (
      <input
        type={inputType}
        id={fieldId}
        value={value}
        min={schema.minimum}
        max={schema.maximum}
        step={schema.type === 'integer' ? 1 : schema.type === 'number' ? 'any' : undefined}
        onChange={(e) => {
          let nextValue = e.target.value;

          if (schema.type === 'integer') {
            nextValue = nextValue === '' ? '' : Number.parseInt(nextValue, 10);
          } else if (schema.type === 'number') {
            nextValue = nextValue === '' ? '' : Number.parseFloat(nextValue);
          }

          handleAnalysisParamChange(paramName, nextValue);
        }}
        className="mt-1 block w-full p-1.5 border border-borderDefault rounded-md shadow-sm text-sm bg-backgroundInput text-textPrimary"
      />
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError(null);

    let jobData = {
      name: jobName,
      instrument_symbol: selectedInstrument,
    };

    if (hasPlanDraft) {
      if (!selectedInstrument) {
        const errorMsg = 'Instrument is required before submitting a planned analysis.';
        setFormError(errorMsg);
        if (onError) onError(errorMsg);
        return;
      }

      try {
        const parsedSpec = parseActiveDraftSpec();
        if (activeDraft?.persistedDraftId) {
          const syncedDraft = await updateAnalysisDraft(
            activeDraft.persistedDraftId,
            buildDraftPersistencePayload(parsedSpec)
          );
          upsertSavedDraft(syncedDraft);
          setActiveDraft(buildPersistedAnalysisDraft(syncedDraft));
          jobData = {
            ...jobData,
            persistedDraftId: syncedDraft.draft_id,
          };
        } else {
          jobData = {
            ...jobData,
            spec: parsedSpec,
          };
        }
      } catch (error) {
        const errorMsg = `Plan spec is invalid JSON: ${error.message}`;
        setFormError(errorMsg);
        if (onError) onError(errorMsg);
        return;
      }
    } else {
      if (!selectedInstrument || !selectedAnalysisType || !targetTimeframe || !startDate || !endDate) {
        const errorMsg = 'All required fields must be filled.';
        setFormError(errorMsg);
        if (onError) onError(errorMsg);
        return;
      }
      if (new Date(startDate) >= new Date(endDate)) {
        const errorMsg = 'End date must be after start date.';
        setFormError(errorMsg);
        if (onError) onError(errorMsg);
        return;
      }

      jobData = {
        ...jobData,
        analysis_type: selectedAnalysisType,
        target_timeframe: targetTimeframe,
        start_date: startDate,
        end_date: endDate,
        indicator_configs: indicatorConfigs,
        analysis_params: analysisParams,
      };
    }

    setIsSubmitting(true);
    try {
      const result = hasPlanDraft && jobData.persistedDraftId
        ? await submitAnalysisDraft(jobData.persistedDraftId, {
          name: jobName,
          instrument_symbol: selectedInstrument,
        })
        : await submitAnalysisJob(jobData);
      if (onJobSubmitted) onJobSubmitted(result);
      // Reset only some fields for convenience
      setJobName('');
      setSelectedInstrument('');
      setStartDate('');
      setEndDate('');
      setPlannerPrompt('');
      setPlannerAnalysisTypeHint('');
      setPlannerTimeframeHint('');
      clearPlanDraft();
    } catch (error) {
      const errorMessage = `Submission failed: ${error.message}`;
      setFormError(errorMessage);
      if (onError) onError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {formError && <p className="rounded-xl bg-red-100 p-3 text-red-500">{formError}</p>}

      {activeDraft?.sourceType === 'recovery' && (
        <div className="rounded-xl border border-sky-200 bg-sky-50 p-3 text-sm text-sky-700">
          <span className="font-semibold">Recovery Draft Loaded.</span>
          {' '}
          {activeDraft.sourceLabel}
          {activeDraft.sourceJobId ? ` from job ${activeDraft.sourceJobId}.` : '.'}
        </div>
      )}

      {activeDraft?.sourceType === 'template' && (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-700">
          <span className="font-semibold">Template Draft Loaded.</span>
          {' '}Review and submit when ready.
        </div>
      )}

      <div className="rounded-2xl border border-borderDefault bg-backgroundBody p-4 space-y-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <h3 className="text-base font-semibold text-textPrimary">Prompt Builder</h3>
          {activeDraft && (
            <div className="flex flex-wrap gap-2 text-xs">
              <span className={`rounded-full px-2.5 py-1 font-medium ${getPlannerOutcomeClasses(activePlannerOutcome, activeDraft.planReady, activeReviewPosture)}`}>
                {getPlannerOutcomeLabel(activePlannerOutcome, activeDraft.planReady, activeReviewPosture)}
              </span>
              {activeReviewPosture?.current_executor_support ? (
                <span className={`rounded-full px-2.5 py-1 font-medium ${getPlannerSupportClasses(activeReviewPosture.current_executor_support, activeReviewPosture)}`}>
                  {getPlannerSupportLabel(activeReviewPosture.current_executor_support, activeReviewPosture)}
                </span>
              ) : null}
              <span className={`rounded-full px-2.5 py-1 font-medium ${getPlannerReadinessClasses(activeDraft)}`}>
                {getPlannerReadinessLabel(activeDraft)}
              </span>
            </div>
          )}
        </div>

        <div>
          <label htmlFor="plannerPrompt" className="block text-sm font-medium text-textSecondary">Research Question</label>
          <textarea
            id="plannerPrompt"
            rows={4}
            ref={plannerPromptRef}
            value={plannerPrompt}
            onChange={(e) => setPlannerPrompt(e.target.value)}
            className="mt-1 block w-full rounded-xl border border-borderDefault bg-backgroundInput p-3 shadow-sm text-textPrimary"
            placeholder="Describe the condition, outcome window, and context you want to test."
          />
        </div>

        <details className="rounded-xl border border-borderDefault/70 bg-backgroundSurface p-3">
          <summary className="cursor-pointer text-sm font-medium text-textSecondary">Optional Planner Hints</summary>

          <div className="mt-3 grid gap-4 md:grid-cols-2">
            <div>
              <label htmlFor="plannerAnalysisTypeHint" className="block text-sm font-medium text-textSecondary">Analysis Type Hint (optional)</label>
              <select
                id="plannerAnalysisTypeHint"
                value={plannerAnalysisTypeHint}
                onChange={(e) => setPlannerAnalysisTypeHint(e.target.value)}
                className="mt-1 block w-full rounded-xl border border-borderDefault bg-backgroundInput p-2.5 shadow-sm text-textPrimary"
              >
                <option value="">Let the planner infer it</option>
                {analysisTypes.map((type) => (
                  <option key={type.value} value={type.value}>{type.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="plannerTimeframeHint" className="block text-sm font-medium text-textSecondary">Timeframe Hint (optional)</label>
              <select
                id="plannerTimeframeHint"
                value={plannerTimeframeHint}
                onChange={(e) => setPlannerTimeframeHint(e.target.value)}
                className="mt-1 block w-full rounded-xl border border-borderDefault bg-backgroundInput p-2.5 shadow-sm text-textPrimary"
              >
                <option value="">Let the planner choose</option>
                {availableTimeframes.map((tf) => <option key={tf} value={tf}>{tf}</option>)}
              </select>
            </div>
          </div>
        </details>

        <div className="flex flex-wrap gap-3">
          <button
            type="button"
            onClick={handlePlanRequest}
            disabled={isPlanning || isSubmitting || isSavingDraft}
            className="rounded-xl border border-transparent bg-primary px-4 py-2.5 text-sm font-medium text-white shadow-sm hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-dark disabled:opacity-50"
          >
            {isPlanning ? 'Planning...' : planningActionLabel}
          </button>
          {activeDraft && (
            <button
              type="button"
              onClick={handleSaveDraft}
              disabled={!hasPlanDraft || isPlanning || isSubmitting || isSavingDraft}
              className="rounded-xl border border-borderDefault bg-backgroundSurface px-4 py-2.5 text-sm font-medium text-textPrimary shadow-sm hover:bg-backgroundBody disabled:opacity-50"
            >
              {isSavingDraft
                ? 'Saving Draft...'
                : activeDraft.persistedDraftId
                  ? 'Save Draft Changes'
                  : 'Save Draft'}
            </button>
          )}
          {activeDraft && (
            <button
              type="button"
              onClick={clearPlanDraft}
              className="rounded-xl border border-borderDefault bg-backgroundSurface px-4 py-2.5 text-sm font-medium text-textPrimary shadow-sm hover:bg-backgroundBody"
            >
              Clear Draft
            </button>
          )}
        </div>
      </div>

      <div className="rounded-2xl border border-borderDefault bg-backgroundBody p-4 space-y-4">
        <div>
          <h3 className="text-base font-semibold text-textPrimary">Run Settings</h3>
          <p className="mt-1 text-sm text-textSecondary">Scope the instrument, timeframe, and dates for the study you want to run.</p>
        </div>

        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          <div>
            <label htmlFor="jobName" className="block text-sm font-medium text-textSecondary">Job Name</label>
            <input type="text" id="jobName" value={jobName} onChange={(e) => setJobName(e.target.value)} className="mt-1 block w-full rounded-xl border border-borderDefault bg-backgroundInput p-2.5 shadow-sm text-textPrimary" placeholder="Optional" />
          </div>

          <div>
            <label htmlFor="instrument" className="block text-sm font-medium text-textSecondary">Instrument</label>
            <select id="instrument" value={selectedInstrument} onChange={(e) => setSelectedInstrument(e.target.value)} className="mt-1 block w-full rounded-xl border border-borderDefault bg-backgroundInput p-2.5 shadow-sm text-textPrimary" disabled={instruments.length === 0}>
              <option value="" disabled>Select an instrument</option>
              {instruments.map((inst) => (
                <option key={inst.symbol} value={inst.symbol} disabled={inst.data_status !== 'AVAILABLE'}>
                  {inst.symbol} ({inst.exchange}) - {inst.data_status === 'AVAILABLE' ? 'Available' : 'Data Not Ready'}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="targetTimeframe" className="block text-sm font-medium text-textSecondary">Target Timeframe</label>
            <select id="targetTimeframe" value={targetTimeframe} onChange={(e) => setTargetTimeframe(e.target.value)} className="mt-1 block w-full rounded-xl border border-borderDefault bg-backgroundInput p-2.5 shadow-sm text-textPrimary" disabled={hasPlanDraft}>
              {availableTimeframes.map((tf) => <option key={tf} value={tf}>{tf}</option>)}
            </select>
          </div>

          <div>
            <label htmlFor="startDate" className="block text-sm font-medium text-textSecondary">Start Date</label>
            <input type="date" id="startDate" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="mt-1 block w-full rounded-xl border border-borderDefault bg-backgroundInput p-2.5 shadow-sm text-textPrimary" disabled={hasPlanDraft} />
          </div>

          <div>
            <label htmlFor="endDate" className="block text-sm font-medium text-textSecondary">End Date</label>
            <input type="date" id="endDate" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="mt-1 block w-full rounded-xl border border-borderDefault bg-backgroundInput p-2.5 shadow-sm text-textPrimary" disabled={hasPlanDraft} />
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-borderDefault bg-backgroundBody p-4 space-y-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <h3 className="text-base font-semibold text-textPrimary">Library</h3>
          <div className="inline-flex rounded-full bg-backgroundSurface p-1">
            <button
              type="button"
              onClick={() => setLibraryView('drafts')}
              className={`rounded-full px-3 py-1.5 text-xs font-medium transition ${libraryView === 'drafts' ? 'bg-primary text-white' : 'text-textSecondary'}`}
            >
              Drafts
            </button>
            <button
              type="button"
              onClick={() => setLibraryView('templates')}
              className={`rounded-full px-3 py-1.5 text-xs font-medium transition ${libraryView === 'templates' ? 'bg-primary text-white' : 'text-textSecondary'}`}
            >
              Templates
            </button>
          </div>
        </div>

        {libraryView === 'drafts' ? (
          <div>
            {isLoadingDrafts ? <span className="text-xs text-textSecondary">Loading drafts...</span> : null}
            {!isLoadingDrafts && savedDrafts.length === 0 ? (
              <p className="text-sm text-textSecondary">No saved drafts yet.</p>
            ) : null}
            {savedDrafts.length > 0 ? (
              <ul className="space-y-2">
                {savedDrafts.map((draft) => (
                  <li key={draft.draft_id} className="flex flex-col gap-2 rounded-xl border border-borderDefault/60 bg-backgroundSurface p-3 md:flex-row md:items-center md:justify-between">
                    <div>
                      <p className="text-sm font-medium text-textPrimary">{draft.name || draft.analysis_type || 'Saved draft'}</p>
                      <p className="mt-1 text-xs text-textSecondary">
                        {draft.analysis_type || 'Unresolved type'}
                        {draft.target_timeframe ? ` · ${draft.target_timeframe}` : ''}
                        {draft.instrument_symbol ? ` · ${draft.instrument_symbol}` : ''}
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => handleLoadSavedDraft(draft)}
                        className="rounded-md border border-borderDefault bg-backgroundBody px-3 py-1.5 text-xs font-medium text-textPrimary hover:bg-backgroundSurface"
                      >
                        Load Draft
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDeleteSavedDraft(draft.draft_id)}
                        className="rounded-md border border-rose-200 bg-rose-50 px-3 py-1.5 text-xs font-medium text-rose-700 hover:bg-rose-100"
                      >
                        Delete
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        ) : (
          <div>
            {isLoadingTemplates ? <span className="text-xs text-textSecondary">Loading templates...</span> : null}
            {!isLoadingTemplates && savedTemplates.length === 0 ? (
              <p className="text-sm text-textSecondary">No saved templates yet.</p>
            ) : null}
            {savedTemplates.length > 0 ? (
              <ul className="space-y-2">
                {savedTemplates.map((template) => (
                  <li key={template.template_id} className="flex flex-col gap-2 rounded-xl border border-borderDefault/60 bg-backgroundSurface p-3 md:flex-row md:items-center md:justify-between">
                    <div>
                      <p className="text-sm font-medium text-textPrimary">{template.name || 'Saved template'}</p>
                      <p className="mt-1 text-xs text-textSecondary">
                        {prettifyAnalysisType(template.base_analysis_type)}
                        {template.latest_version_number ? ` · v${template.latest_version_number}` : ''}
                        {template.latest_version?.target_timeframe ? ` · ${template.latest_version.target_timeframe}` : ''}
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {template.version_count > 1 ? (
                        <div className="flex items-center gap-2">
                          <select
                            aria-label={`Version for ${template.name || 'saved template'}`}
                            value={selectedTemplateVersions[template.template_id] || template.latest_version_number || ''}
                            onFocus={() => {
                              void ensureTemplateVersionsLoaded(template);
                            }}
                            onChange={(event) => setSelectedTemplateVersions((currentSelections) => ({
                              ...currentSelections,
                              [template.template_id]: Number.parseInt(event.target.value, 10),
                            }))}
                            className="rounded-md border border-borderDefault bg-backgroundBody px-2 py-1.5 text-xs font-medium text-textPrimary"
                          >
                            {getTemplateVersionOptions(template).map((version) => (
                              <option key={`${template.template_id}-v${version.version_number}`} value={version.version_number}>
                                {version.version_number === template.latest_version_number
                                  ? `Latest (v${version.version_number})`
                                  : `v${version.version_number}`}
                                {version.target_timeframe ? ` · ${version.target_timeframe}` : ''}
                              </option>
                            ))}
                          </select>
                          {loadingTemplateVersionsId === template.template_id ? (
                            <span className="text-xs text-textSecondary">Loading versions...</span>
                          ) : null}
                        </div>
                      ) : null}
                      <button
                        type="button"
                        onClick={() => handleInstantiateTemplate(template)}
                        disabled={Boolean(instantiatingTemplateId)}
                        className="rounded-md border border-borderDefault bg-backgroundBody px-3 py-1.5 text-xs font-medium text-textPrimary hover:bg-backgroundSurface disabled:opacity-50"
                      >
                        {instantiatingTemplateId === template.template_id ? 'Loading Template...' : 'Load Template'}
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        )}
      </div>

      {activeDraft && (
        <div className="space-y-4 rounded-2xl border border-borderDefault bg-backgroundBody p-4">
          <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
            <div>
              <h3 className="text-base font-semibold text-textPrimary">Plan Review</h3>
              {activeDraftAnalysisTypeLabel && (
                <p className="mt-1 text-sm text-textSecondary">Resolved Type: {activeDraftAnalysisTypeLabel}</p>
              )}
            </div>
            <p className="text-xs text-textSecondary">
              {activeDraft.plannerSource || 'deterministic_prompt_router_v1'}
              {activeDraft.plannerModel ? ` (${activeDraft.plannerModel})` : ''}
            </p>
          </div>

          <div className={`rounded-xl border p-4 ${getPlannerPanelClasses(activePlannerOutcome, activeDraft.planReady, activeReviewPosture)}`}>
            <div className="flex flex-wrap items-center gap-2 text-xs font-medium">
              <span className={`rounded-full px-2.5 py-1 ${getPlannerOutcomeClasses(activePlannerOutcome, activeDraft.planReady, activeReviewPosture)}`}>
                {getPlannerOutcomeLabel(activePlannerOutcome, activeDraft.planReady, activeReviewPosture)}
              </span>
              {activeReviewPosture?.current_executor_support ? (
                <span className={`rounded-full px-2.5 py-1 ${getPlannerSupportClasses(activeReviewPosture.current_executor_support, activeReviewPosture)}`}>
                  {getPlannerSupportLabel(activeReviewPosture.current_executor_support, activeReviewPosture)}
                </span>
              ) : null}
            </div>

            {activeReviewPosture?.explanation?.title ? (
              <div className="mt-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-textSecondary">Planner Guidance</p>
                <p className="mt-1 text-sm font-medium text-textPrimary">{activeReviewPosture.explanation.title}</p>
              </div>
            ) : null}

            <p className="mt-3 text-sm text-textPrimary">{buildPlannerAssistantSummary(activeDraft)}</p>

            {activeReviewPosture?.pricing?.summary ? (
              <div className="mt-3 rounded-lg border border-borderDefault bg-backgroundInput p-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-textSecondary">Pricing Guidance</p>
                <p className="mt-1 text-sm text-textPrimary">{activeReviewPosture.pricing.summary}</p>
              </div>
            ) : null}

            {Array.isArray(activeReviewPosture?.execution_considerations) && activeReviewPosture.execution_considerations.length > 0 ? (
              <div className="mt-3 rounded-lg border border-borderDefault bg-backgroundInput p-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-textSecondary">Execution Considerations</p>
                <ul className="mt-2 list-disc list-inside space-y-1 text-sm text-textPrimary">
                  {activeReviewPosture.execution_considerations.map((consideration) => (
                    <li key={consideration}>{consideration}</li>
                  ))}
                </ul>
              </div>
            ) : null}

            {activeClarifyingQuestion?.question ? (
              <div className="mt-4 space-y-3">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-textSecondary">Planner Question</p>
                  <p className="mt-1 text-sm font-medium text-textPrimary">{activeClarifyingQuestion.question}</p>
                </div>
                {Array.isArray(activeClarifyingQuestion.choices) && activeClarifyingQuestion.choices.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {activeClarifyingQuestion.choices.map((choice) => (
                      <button
                        key={choice}
                        type="button"
                        onClick={() => handleClarifyingChoiceSelect(choice)}
                        className="rounded-full border border-borderDefault bg-backgroundSurface px-3 py-1.5 text-xs font-medium text-textPrimary hover:bg-backgroundBody"
                      >
                        {choice}
                      </button>
                    ))}
                  </div>
                ) : null}
                <p className="text-xs text-textSecondary">
                  {isCapabilityOverviewReview(activeReviewPosture)
                    ? 'Pick a starter option or describe a specific study in the research box.'
                    : `Reply in the research box and click ${planningActionLabel}.`}
                </p>
              </div>
            ) : null}

            {activeReviewPosture ? (
              <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                <div className="rounded-lg border border-borderDefault bg-backgroundInput p-3">
                  <p className="text-xs font-semibold uppercase tracking-wide text-textSecondary">Review Reason</p>
                  <p className="mt-1 text-sm text-textPrimary">{formatPlannerReason(activeReviewPosture.reason)}</p>
                </div>
                <div className="rounded-lg border border-borderDefault bg-backgroundInput p-3">
                  <p className="text-xs font-semibold uppercase tracking-wide text-textSecondary">Current Support</p>
                  <p className="mt-1 text-sm text-textPrimary">{getPlannerSupportLabel(activeReviewPosture.current_executor_support, activeReviewPosture)}</p>
                </div>
                <div className="rounded-lg border border-borderDefault bg-backgroundInput p-3">
                  <p className="text-xs font-semibold uppercase tracking-wide text-textSecondary">Submission</p>
                  <p className="mt-1 text-sm text-textPrimary">{activeReviewPosture.submit_allowed ? 'Ready to submit' : activeReviewPosture.requires_confirmation ? 'Confirm before submit' : 'Keep refining first'}</p>
                </div>
                <div className="rounded-lg border border-borderDefault bg-backgroundInput p-3">
                  <p className="text-xs font-semibold uppercase tracking-wide text-textSecondary">Next Step</p>
                  <p className="mt-1 text-sm text-textPrimary">{formatPlannerAction(activeReviewPosture.explanation?.recommended_next_action)}</p>
                </div>
                <div className="rounded-lg border border-borderDefault bg-backgroundInput p-3">
                  <p className="text-xs font-semibold uppercase tracking-wide text-textSecondary">Review Surface</p>
                  <p className="mt-1 text-sm text-textPrimary">{getPlannerReviewSurfaceLabel(activePlannerRepresentation)}</p>
                </div>
                <div className="rounded-lg border border-borderDefault bg-backgroundInput p-3">
                  <p className="text-xs font-semibold uppercase tracking-wide text-textSecondary">Planner Intent</p>
                  <p className="mt-1 text-sm text-textPrimary">{activeDraft?.queryPlan?.intent ? prettifyAnalysisType(activeDraft.queryPlan.intent) : 'Not specified'}</p>
                </div>
              </div>
            ) : null}

            {Array.isArray(activeReviewPosture?.complexity_features) && activeReviewPosture.complexity_features.length > 0 ? (
              <div className="mt-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-textSecondary">Complexity Signals</p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {activeReviewPosture.complexity_features.map((feature) => (
                    <span key={feature} className="rounded-full border border-borderDefault bg-backgroundInput px-2.5 py-1 text-xs font-medium text-textPrimary">
                      {formatComplexityFeature(feature)}
                    </span>
                  ))}
                </div>
              </div>
            ) : null}
          </div>

          {Array.isArray(activeDraft.assumptions) && activeDraft.assumptions.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-textPrimary">Assumptions</h4>
              <ul className="mt-2 list-disc list-inside space-y-1 text-sm text-textSecondary">
                {activeDraft.assumptions.map((assumption) => (
                  <li key={assumption}>{assumption}</li>
                ))}
              </ul>
            </div>
          )}

          {Array.isArray(activeDraft.warnings) && activeDraft.warnings.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-amber-700">Warnings</h4>
              <ul className="mt-2 list-disc list-inside space-y-1 text-sm text-amber-700">
                {activeDraft.warnings.map((warning) => (
                  <li key={warning}>{warning}</li>
                ))}
              </ul>
            </div>
          )}

          {Array.isArray(activeDraft.missingDetails) && activeDraft.missingDetails.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-textPrimary">Missing Details</h4>
              <div className="mt-2 flex flex-wrap gap-2">
                {activeDraft.missingDetails.map((detail) => (
                  <span key={detail} className="rounded-full bg-gray-100 px-2 py-1 text-xs font-medium text-gray-700">
                    {detail.replace(/_/g, ' ')}
                  </span>
                ))}
              </div>
            </div>
          )}

          {Array.isArray(activeDraft.candidateAnalysisTypes) && activeDraft.candidateAnalysisTypes.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-textPrimary">Candidate Analysis Types</h4>
              <div className="mt-2 flex flex-wrap gap-2">
                {activeDraft.candidateAnalysisTypes.map((candidate) => (
                  <button
                    key={candidate.value}
                    type="button"
                    onClick={() => setPlannerAnalysisTypeHint(candidate.value)}
                    className="rounded-full border border-borderDefault bg-backgroundSurface px-2 py-1 text-xs font-medium text-textPrimary hover:bg-backgroundBody"
                  >
                    {candidate.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {hasPlanDraft && (
            <div>
              <label htmlFor="planSpecText" className="block text-sm font-medium text-textSecondary">{getPlanSpecEditorLabel(activePlannerRepresentation)}</label>
              {getPlanSpecEditorHelpText(activePlannerRepresentation) ? (
                <p className="mt-1 text-xs text-textSecondary">{getPlanSpecEditorHelpText(activePlannerRepresentation)}</p>
              ) : null}
              <textarea
                id="planSpecText"
                rows={14}
                ref={planSpecTextRef}
                value={activeDraft.specText}
                onChange={(e) => setActiveDraft((currentDraft) => (
                  currentDraft
                    ? { ...currentDraft, specText: e.target.value }
                    : currentDraft
                ))}
                className="mt-1 block w-full rounded-xl border border-borderDefault bg-backgroundInput p-2 shadow-sm font-mono text-xs text-textPrimary"
              />
            </div>
          )}
        </div>
      )}

      {isLoadingDetails && <p className="text-textSecondary">Loading analysis details...</p>}

      {showManualBuilder && (
        <details className="rounded-2xl border border-borderDefault bg-backgroundBody p-4">
          <summary className="cursor-pointer text-sm font-semibold text-textPrimary">Manual Builder</summary>

          <div className="mt-4 space-y-4">
            <div className="space-y-2">
              <p className="text-sm text-textSecondary">Use this only when you want to configure a built-in analysis directly instead of drafting from natural language.</p>
              <div>
                <label htmlFor="analysisType" className="block text-sm font-medium text-textSecondary">Analysis Type</label>
                <select id="analysisType" value={selectedAnalysisType} onChange={(e) => setSelectedAnalysisType(e.target.value)} className="mt-1 block w-full rounded-xl border border-borderDefault bg-backgroundInput p-2.5 shadow-sm text-textPrimary" disabled={analysisTypes.length === 0}>
                  <option value="">Select a built-in analysis</option>
                  {analysisTypes.map((type) => <option key={type.value} value={type.value}>{type.label}</option>)}
                </select>
              </div>
            </div>

            {!selectedAnalysisType && (
              <p className="text-sm text-textSecondary">Choose an analysis type to load manual indicators and parameters.</p>
            )}

            {selectedAnalysisType && indicatorConfigs.length > 0 && (
              <div className="space-y-4">
                <h3 className="text-sm font-semibold text-textPrimary">Indicator Settings</h3>
                {indicatorConfigs.map((config, index) => (
                  <div key={index} className="rounded-xl border border-borderDefault/50 bg-backgroundSurface p-3">
                    <p className="font-medium text-textPrimary">{config.name} ({config.output_name})</p>
                    <div className="mt-2 grid grid-cols-1 gap-x-4 gap-y-2 md:grid-cols-2">
                      {Object.entries(config.params).map(([paramName, paramValue]) => (
                        <div key={paramName}>
                          <label htmlFor={`indicator-${index}-${paramName}`} className="block text-xs font-medium capitalize text-textSecondary">{paramName}</label>
                          <input
                            type="text"
                            id={`indicator-${index}-${paramName}`}
                            value={paramValue}
                            onChange={(e) => handleIndicatorParamChange(index, paramName, e.target.value)}
                            className="mt-1 block w-full rounded-md border border-borderDefault bg-backgroundInput p-1.5 text-sm text-textPrimary shadow-sm"
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {selectedAnalysisType && Object.keys(analysisParamSchema).length > 0 && (
              <div className="space-y-4">
                <h3 className="text-sm font-semibold text-textPrimary">Analysis Parameters</h3>
                <div className="grid grid-cols-1 gap-x-4 gap-y-2 md:grid-cols-2">
                  {Object.entries(analysisParamSchema).map(([paramName, schema]) => (
                    <div key={paramName}>
                      <label htmlFor={`analysis-param-${paramName}`} className="block text-xs font-medium capitalize text-textSecondary">
                        {schema.label || paramName.replace(/_/g, ' ')}
                      </label>
                      {renderAnalysisParamInput(paramName, schema)}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {selectedAnalysisType && Object.keys(analysisParamSchema).length === 0 && Object.keys(analysisParams).length > 0 && (
              <div className="space-y-4">
                <h3 className="text-sm font-semibold text-textPrimary">Analysis Parameters</h3>
                <div className="grid grid-cols-1 gap-x-4 gap-y-2 md:grid-cols-2">
                  {Object.entries(analysisParams).map(([paramName, paramValue]) => (
                    <div key={paramName}>
                      <label htmlFor={`analysis-param-${paramName}`} className="block text-xs font-medium capitalize text-textSecondary">{paramName.replace(/_/g, ' ')}</label>
                      <input
                        type="text"
                        id={`analysis-param-${paramName}`}
                        value={paramValue}
                        onChange={(e) => handleAnalysisParamChange(paramName, e.target.value)}
                        className="mt-1 block w-full rounded-md border border-borderDefault bg-backgroundInput p-1.5 text-sm text-textPrimary shadow-sm"
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </details>
      )}

      {hasPlanDraft && (
        <div className="rounded-2xl border border-borderDefault bg-backgroundBody p-3 text-sm text-textSecondary">
          Manual builder is hidden while a reviewed plan draft is active.
        </div>
      )}

      <button type="submit" disabled={isSubmitting || isLoadingDetails || isPlanning || instruments.length === 0 || (!hasPlanDraft && !isManualSubmitReady)} className="w-full rounded-xl border border-transparent bg-primary px-4 py-3 text-sm font-medium text-white shadow-sm hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-dark disabled:opacity-50">
        {isSubmitting ? 'Submitting...' : hasPlanDraft ? 'Submit Planned Analysis' : 'Submit Manual Analysis'}
      </button>
    </form>
  );
};

export default AnalysisJobForm;
